import { sendModalMsg } from '@molecules/ModalMessages';
import { ConfigService } from '@/configuration/services/configService';
import { BASE_PATH } from '@/configuration';

/**
 * Checks for app version updates by monitoring the last-modified header of index.html.
 * Shows a notification when a new version is detected.
 */
export class VersionCheckHandler {
    private lastModified: string | null = null;
    private intervalId: number | null = null;
    private isNotificationShown: boolean = false;
    private readonly enabled: boolean = false;
    private readonly checkInterval: number = 120 * 1000; // in milliseconds
    private visibilityChangeHandler: (() => void) | null = null;

    constructor() {
        try {
            const config = ConfigService.get();
            this.enabled = config.application.versionCheck?.versionCheckEnabled ?? false;
            this.checkInterval = config.application.versionCheck?.versionCheckInterval ?? 120 * 1000;
        } catch {
            console.warn(
                'Config not available during VersionCheck initialization, defaulting to disabled, interval to 120s',
            );
            this.enabled = false;
            this.checkInterval = 120 * 1000;
        }
    }

    /**
     * Initialize version checking
     */
    public init(): void {
        if (!this.enabled) {
            console.log('Version checking is disabled');
            return;
        }

        // Get initial version
        this.getCurrentVersion().then((version) => {
            this.lastModified = version;
            console.log('Initial version detected:', version);
        });

        // Start periodic checks
        this.startPeriodicCheck();

        // Add visibility change listener
        this.addVisibilityChangeListener();
    }

    /**
     * Stop version checking
     */
    public destroy(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }

        // Remove visibility change listener
        this.removeVisibilityChangeListener();
    }

    /**
     * Check if the current tab is active
     */
    private isTabActive(): boolean {
        return document.visibilityState === 'visible';
    }

    /**
     * Add listener for tab visibility changes
     */
    private addVisibilityChangeListener(): void {
        this.visibilityChangeHandler = async () => {
            // Only check when tab becomes visible (active)
            if (this.isTabActive()) {
                await this.checkVersion();
            }
        };

        document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    }

    /**
     * Remove visibility change listener
     */
    private removeVisibilityChangeListener(): void {
        if (this.visibilityChangeHandler) {
            document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
            this.visibilityChangeHandler = null;
        }
    }

    /**
     * Perform version check
     */
    private async checkVersion(): Promise<void> {
        try {
            const currentVersion = await this.getCurrentVersion();

            if (this.lastModified && currentVersion && this.lastModified !== currentVersion) {
                console.log('New version detected:', currentVersion, 'Previous:', this.lastModified);

                if (!this.isNotificationShown) {
                    this.showUpdateNotification();
                }

                this.lastModified = currentVersion;
            }
        } catch (error) {
            console.warn('Version check failed:', error);
        }
    }

    /**
     * Start periodic version checking
     */
    private startPeriodicCheck(): void {
        if (!this.enabled) {
            return;
        }

        this.intervalId = window.setInterval(async () => {
            // Only check version when tab is active
            if (!this.isTabActive()) {
                return;
            }

            await this.checkVersion();
        }, this.checkInterval);
    }

    /**
     * Get current version by checking version.json file
     * Generated during build time
     */
    private async getCurrentVersion(): Promise<string | null> {
        try {
            // Добавляем timestamp, чтобы гарантированно избежать кэширования
            const cacheBuster = new Date().getTime();

            const versionUrl = `.${BASE_PATH === '/' ? '/' : BASE_PATH + '/'}version.json?t=${cacheBuster}`;

            const response = await fetch(versionUrl, {
                method: 'GET',
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    Pragma: 'no-cache',
                    Expires: '0',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Ожидаем структуру { "version": "..." }
            const data: { version: string } = await response.json();

            return data.version;
        } catch (error) {
            console.warn('Failed to fetch version info:', error);
            return null;
        }
    }

    /**
     * Show notification about available update
     */
    private showUpdateNotification(): void {
        this.isNotificationShown = true;

        sendModalMsg({
            message: [
                'Мы обновили сервис, чтобы сделать его лучше. Перезагрузите страницу, чтобы всё работало правильно.',
            ],
            type: 'info',
            title: 'Обновление сервиса',
            buttons: [
                {
                    title: 'Перезагрузить страницу',
                    onClick: () => {
                        window.location.reload();
                    },
                    buttonType: 'primary',
                },
            ],
            closeBtnTitle: 'Остаться',
            closeBtnType: 'light',
            closeBtnClick: () => {},
        });
    }
}

/**
 * Global instance of version checker
 */
let versionChecker: VersionCheckHandler | null = null;

/**
 * Initialize version checking
 */
export const initVersionCheckHandler = (): void => {
    if (versionChecker) {
        versionChecker.destroy();
    }

    versionChecker = new VersionCheckHandler();
    versionChecker.init();
};

/**
 * Stop version checking
 */
export const destroyVersionCheckHandler = (): void => {
    if (versionChecker) {
        versionChecker.destroy();
        versionChecker = null;
    }
};
